import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControl,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import React, { forwardRef, FC, HTMLAttributes, useMemo } from 'react';
import strings from '../../../../../../common/Translation/Translate';
import { DropdownOption } from './priceFormUtils';

type WeekDaysMultiSelectProps = {
  id: string;
  label: string;
  options: DropdownOption[];
  value: DropdownOption[];
  onChange: (value: DropdownOption[]) => void;
  hasError?: boolean;
};

const chipSx = {
  height: 24,
  '& .MuiChip-label': { fontSize: '0.8125rem', px: 0.75 },
};

type ListboxWithSelectAllProps = HTMLAttributes<HTMLUListElement> & {
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
};

const ListboxWithSelectAll = forwardRef<HTMLUListElement, ListboxWithSelectAllProps>(
  function ListboxWithSelectAll({ children, allSelected, someSelected, onToggleAll, ...other }, ref) {
    return (
      <ul ref={ref} {...other}>
        <ListItemButton
          dense
          onMouseDown={e => e.preventDefault()}
          onClick={onToggleAll}
          sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Checkbox
              edge="start"
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText
            primary={strings.venueConfig_price_weekDays_selectAll}
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </ListItemButton>
        {children}
      </ul>
    );
  },
);

export const WeekDaysMultiSelect: FC<WeekDaysMultiSelectProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  hasError = false,
}) => {
  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0 && !allSelected;

  const Listbox = useMemo(
    () =>
      forwardRef<HTMLUListElement, HTMLAttributes<HTMLUListElement>>(function WeekDaysListbox(props, ref) {
        return (
          <ListboxWithSelectAll
            ref={ref}
            {...props}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleAll={() => onChange(allSelected ? [] : [...options])}
          />
        );
      }),
    [allSelected, someSelected, onChange, options],
  );

  return (
    <FormControl fullWidth error={hasError} size="small">
      <Autocomplete
        id={id}
        multiple
        size="small"
        disableCloseOnSelect
        options={options}
        value={value}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
        onChange={(_, newValue) => onChange(newValue)}
        ListboxComponent={Listbox}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox size="small" sx={{ mr: 0.75, p: 0.25 }} checked={selected} tabIndex={-1} disableRipple />
            {option.label}
          </li>
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              size="small"
              label={option.label}
              sx={chipSx}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            size="small"
            error={hasError}
            helperText={hasError ? strings.requiredText : undefined}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 40,
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                py: 0.5,
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};
